/**
 * UpgradeComparisonCard — Current tier vs. next tier comparison
 *
 * Configuration-driven via TIER_CONFIG (subscriptionTiers.ts).
 * Shows only when an upgrade path exists (canUpgrade === true).
 * Used inside SubscriptionDrawer (web + native) and potentially inline in chat.
 *
 * Mission 33 — Subscription System
 * Created: February 2026
 */

import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import {
  getTierConfig,
  getUpgradeTier,
  formatPrice,
  type TierConfig,
} from '@/config/subscriptionTiers';
import type { SubscriptionTier, SubscriptionPeriod } from '@/services/subscriptionService';
import { CoachCedarColors } from '@/utils/styleConverter';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface UpgradeComparisonCardProps {
  /** User's current subscription tier */
  currentTierId: SubscriptionTier;

  /** Fires when user taps the upgrade CTA */
  onUpgrade: (upgradeTierId: SubscriptionTier) => void;

  /** Optional: override the billing period toggle default */
  defaultPeriod?: SubscriptionPeriod;

  /** Optional: compact mode for inline use (e.g. chat or notification) */
  compact?: boolean;

  /**
   * Localized price strings fetched from RevenueCat (via useSubscriptionPrices).
   * When provided these replace the static USD values from TIER_CONFIG so the
   * price shown always matches the user's local App Store / Play Store currency.
   */
  priceStringMonthly?: string;
  priceStringAnnual?: string;

  /** Localized price strings for the CURRENT tier (so we never show hardcoded USD). */
  currentPriceStringMonthly?: string;
  currentPriceStringAnnual?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UpgradeComparisonCard({
  currentTierId,
  onUpgrade,
  defaultPeriod = 'monthly',
  compact = false,
  priceStringMonthly,
  priceStringAnnual,
  currentPriceStringMonthly,
  currentPriceStringAnnual,
}: UpgradeComparisonCardProps) {
  const [period, setPeriod] = useState<SubscriptionPeriod>(defaultPeriod);

  // Look up tiers from config
  let currentTier: TierConfig;
  let upgradeTier: TierConfig | null;

  try {
    currentTier = getTierConfig(currentTierId);
    upgradeTier = getUpgradeTier(currentTierId);
  } catch {
    return null; // Unknown tier — render nothing
  }

  // Nothing to upgrade to
  if (!upgradeTier) return null;

  const currentPrice =
    period === 'monthly'
      ? currentTier.pricing.monthly
      : currentTier.pricing.annualMonthly;

  const upgradePrice =
    period === 'monthly'
      ? upgradeTier.pricing.monthly
      : upgradeTier.pricing.annualMonthly;

  const priceDiff = upgradePrice - currentPrice;

  // Use localized price string from RevenueCat when available, fall back to em-dash
  // (never hardcode USD amounts — prices must reflect the user's local currency)
  const upgradePriceDisplay =
    period === 'monthly'
      ? (priceStringMonthly ?? '—')
      : (priceStringAnnual ?? '—');

  // Current tier: use localized price from RevenueCat, fall back to em-dash
  const currentPriceDisplay =
    currentTier.pricing.monthly === 0
      ? 'Free'
      : period === 'monthly'
        ? (currentPriceStringMonthly ?? '—')
        : (currentPriceStringAnnual ?? '—');

  const extraMessages =
    upgradeTier.weeklyMessageLimit - currentTier.weeklyMessageLimit;

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (compact) {
    return (
      <View style={styles.compactContainer}>
        <Text style={styles.compactHeading}>
          Want more coaching time?
        </Text>
        <Text style={styles.compactBody}>
          Upgrade to {upgradeTier.displayName} for{' '}
          <Text style={styles.bold}>
            {extraMessages} extra messages/week
          </Text>
          {priceStringMonthly ? ` — just ${priceStringMonthly}/mo.` : '.'}
        </Text>
        <TouchableOpacity
          style={styles.compactCta}
          onPress={() => onUpgrade(upgradeTier!.id)}
          activeOpacity={0.8}
        >
          <Text style={styles.compactCtaText}>Upgrade</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      {/* Heading */}
      <Text style={styles.heading}>Upgrade your plan</Text>
      <Text style={styles.subheading}>
        Get {extraMessages} more coaching messages every week
      </Text>

      {/* Billing period toggle */}
      <View style={styles.periodToggle}>
        <TouchableOpacity
          style={[
            styles.periodButton,
            period === 'monthly' && styles.periodButtonActive,
          ]}
          onPress={() => setPeriod('monthly')}
          activeOpacity={0.8}
        >
          <Text
            style={[
              styles.periodText,
              period === 'monthly' && styles.periodTextActive,
            ]}
          >
            Monthly
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.periodButton,
            period === 'annual' && styles.periodButtonActive,
          ]}
          onPress={() => setPeriod('annual')}
          activeOpacity={0.8}
        >
          <Text
            style={[
              styles.periodText,
              period === 'annual' && styles.periodTextActive,
            ]}
          >
            Annual
          </Text>
          {upgradeTier.pricing.annualSavingsPercent > 0 && (
            <View style={styles.savingsBadge}>
              <Text style={styles.savingsText}>
                Save {upgradeTier.pricing.annualSavingsPercent}%
              </Text>
            </View>
          )}
        </TouchableOpacity>
      </View>

      {/* Comparison columns */}
      <View style={styles.comparison}>
        {/* Current tier */}
        <View style={[styles.tierColumn, styles.currentColumn]}>
          <View style={styles.currentBadge}>
            <Text style={styles.currentBadgeText}>Current</Text>
          </View>
          <Text style={styles.tierName}>{currentTier.shortLabel}</Text>
          <Text style={styles.tierPrice}>
            {currentPriceDisplay}
          </Text>
          {currentTier.pricing.monthly > 0 && (
            <Text style={styles.tierPeriod}>/mo</Text>
          )}
          <View style={styles.tierFeature}>
            <Text style={styles.featureNumber}>
              {currentTier.weeklyMessageLimit}
            </Text>
            <Text style={styles.featureLabel}>msgs/week</Text>
          </View>
        </View>

        {/* Arrow */}
        <View style={styles.arrowContainer}>
          <Text style={styles.arrow}>→</Text>
        </View>

        {/* Upgrade tier */}
        <View style={[styles.tierColumn, styles.upgradeColumn]}>
          <View style={styles.upgradeBadge}>
            <Text style={styles.upgradeBadgeText}>Upgrade</Text>
          </View>
          <Text style={[styles.tierName, styles.upgradeTierName]}>
            {upgradeTier.shortLabel}
          </Text>
          <Text style={[styles.tierPrice, styles.upgradeTierPrice]}>
            {upgradePriceDisplay}
          </Text>
          <Text style={[styles.tierPeriod, styles.upgradeTierPeriod]}>
            {period === 'annual' && (priceStringMonthly || priceStringAnnual) ? '/yr' : '/mo'}
          </Text>
          <View style={[styles.tierFeature, styles.upgradeFeature]}>
            <Text style={[styles.featureNumber, styles.upgradeFeatureNumber]}>
              {upgradeTier.weeklyMessageLimit}
            </Text>
            <Text style={[styles.featureLabel, styles.upgradeFeatureLabel]}>
              msgs/week
            </Text>
          </View>
        </View>
      </View>

      {/* Differentiators */}
      {upgradeTier.differentiators.length > 0 && (
        <View style={styles.differentiators}>
          {upgradeTier.differentiators.map((d, i) => (
            <View key={i} style={styles.diffRow}>
              <Text style={styles.diffCheck}>✦</Text>
              <Text style={styles.diffText}>{d}</Text>
            </View>
          ))}
        </View>
      )}

      {/* CTA */}
      <TouchableOpacity
        style={styles.ctaButton}
        onPress={() => onUpgrade(upgradeTier!.id)}
        activeOpacity={0.8}
      >
        <Text style={styles.ctaText}>
          {upgradePriceDisplay !== '—'
            ? `Upgrade to ${upgradeTier.displayName} — ${upgradePriceDisplay}${period === 'annual' ? '/yr' : '/mo'}`
            : `Upgrade to ${upgradeTier.displayName}`}
        </Text>
      </TouchableOpacity>

      {/* Fine print */}
      {period === 'annual' && (
        <Text style={styles.finePrint}>
          Billed as {priceStringAnnual ?? '—'}/year.{' '}
          Proration handled by {Platform.OS === 'ios' ? 'Apple' : 'Google'}.
        </Text>
      )}
      {period === 'monthly' && (
        <Text style={styles.finePrint}>
          Cancel or switch anytime. Changes take effect on your next billing
          date.
        </Text>
      )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const CEDAR_GREEN = '#1F6C4F';
const CEDAR_DARK = '#113C2B';
const CEDAR_LIGHT_BG = '#f0f7f1';
const CEDAR_ACCENT = CoachCedarColors.thrive; // mint accent fill (was teal #42A5A5)
const UPGRADE_HIGHLIGHT = '#E8F2ED';
const BORDER_COLOR = '#e2e2e2';

const styles = StyleSheet.create({
  // Card container
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 14,
    padding: 20,
    borderWidth: 1,
    borderColor: BORDER_COLOR,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.08,
        shadowRadius: 8,
      },
      android: {
        elevation: 3,
      },
      web: {
        // @ts-ignore — web-only boxShadow
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      },
    }),
  },

  // Heading
  heading: {
    fontSize: 18,
    fontWeight: '700' as const,
    color: CEDAR_DARK,
    marginBottom: 4,
  },
  subheading: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
  },

  // Period toggle
  periodToggle: {
    flexDirection: 'row',
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 3,
    marginBottom: 18,
  },
  periodButton: {
    flex: 1,
    paddingVertical: 8,
    alignItems: 'center',
    borderRadius: 6,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  periodButtonActive: {
    backgroundColor: '#ffffff',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
      },
      android: {
        elevation: 1,
      },
      web: {
        // @ts-ignore
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      },
    }),
  },
  periodText: {
    fontSize: 13,
    fontWeight: '500' as const,
    color: '#888',
  },
  periodTextActive: {
    color: CEDAR_DARK,
    fontWeight: '600' as const,
  },
  savingsBadge: {
    backgroundColor: CEDAR_ACCENT,
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 6,
  },
  savingsText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700' as const,
  },

  // Comparison columns
  comparison: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  tierColumn: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 14,
    borderRadius: 10,
  },
  currentColumn: {
    backgroundColor: '#fafafa',
    marginRight: 4,
  },
  upgradeColumn: {
    backgroundColor: UPGRADE_HIGHLIGHT,
    borderWidth: 1.5,
    borderColor: CEDAR_GREEN,
    marginLeft: 4,
  },

  // Badges
  currentBadge: {
    backgroundColor: '#ddd',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginBottom: 8,
  },
  currentBadgeText: {
    fontSize: 10,
    fontWeight: '600' as const,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  upgradeBadge: {
    backgroundColor: CEDAR_GREEN,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginBottom: 8,
  },
  upgradeBadgeText: {
    fontSize: 10,
    fontWeight: '600' as const,
    color: '#fff',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // Tier details
  tierName: {
    fontSize: 14,
    fontWeight: '600' as const,
    color: '#888',
    marginBottom: 4,
  },
  upgradeTierName: {
    color: CEDAR_DARK,
  },
  tierPrice: {
    fontSize: 24,
    fontWeight: '700' as const,
    color: '#888',
  },
  upgradeTierPrice: {
    color: CEDAR_GREEN,
  },
  tierPeriod: {
    fontSize: 12,
    color: '#aaa',
    marginBottom: 8,
  },
  upgradeTierPeriod: {
    color: '#666',
  },

  // Feature metric (message count)
  tierFeature: {
    alignItems: 'center',
    backgroundColor: '#eee',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  upgradeFeature: {
    backgroundColor: CEDAR_GREEN + '18', // 10% opacity green
  },
  featureNumber: {
    fontSize: 20,
    fontWeight: '700' as const,
    color: '#888',
  },
  upgradeFeatureNumber: {
    color: CEDAR_GREEN,
  },
  featureLabel: {
    fontSize: 10,
    color: '#888',
    fontWeight: '500' as const,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  upgradeFeatureLabel: {
    color: CEDAR_DARK,
  },

  // Arrow
  arrowContainer: {
    paddingHorizontal: 8,
  },
  arrow: {
    fontSize: 22,
    color: CEDAR_GREEN,
    fontWeight: '700' as const,
  },

  // Differentiators
  differentiators: {
    marginBottom: 16,
  },
  diffRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  diffCheck: {
    color: CEDAR_GREEN,
    fontSize: 14,
    marginRight: 8,
    marginTop: 1,
  },
  diffText: {
    flex: 1,
    fontSize: 13,
    color: '#444',
    lineHeight: 18,
  },

  // CTA
  ctaButton: {
    backgroundColor: CEDAR_GREEN,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center',
    marginBottom: 8,
  },
  ctaText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '700' as const,
  },

  // Fine print
  finePrint: {
    fontSize: 11,
    color: '#999',
    textAlign: 'center',
    lineHeight: 15,
  },

  // Compact mode
  compactContainer: {
    backgroundColor: UPGRADE_HIGHLIGHT,
    borderRadius: 10,
    padding: 14,
    borderWidth: 1,
    borderColor: CEDAR_GREEN + '40',
  },
  compactHeading: {
    fontSize: 14,
    fontWeight: '600' as const,
    color: CEDAR_DARK,
    marginBottom: 4,
  },
  compactBody: {
    fontSize: 13,
    color: '#444',
    lineHeight: 18,
    marginBottom: 10,
  },
  bold: {
    fontWeight: '700' as const,
  },
  compactCta: {
    backgroundColor: CEDAR_GREEN,
    borderRadius: 8,
    paddingVertical: 10,
    alignItems: 'center',
  },
  compactCtaText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600' as const,
  },
});
